"use client";

import type {
  SearchEntry,
  SearchSection as SearchSectionType,
  GlobalSearchEmitFunction,
} from "../types";
import { GlobalSearchEvent } from "../types";
import type { GlobalSearchOutput, SearchableAssetType } from "../../schema";
import {
  getAssetIconByType,
  getAssetIconColor,
  getAssetUrl,
  getResultUrl,
} from "../constants";
import { MenuItem } from "react-aria-components";
import { Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { SearchSection } from "./SearchSection";
import { SearchResult } from "./SearchResult";
import styles from "./SearchDropdown.module.css";

function capitalize(string_: string): string {
  return string_.charAt(0).toUpperCase() + string_.slice(1);
}

function getAppConnectionsUrl(appUrl: string): string {
  const match = appUrl.match(/\/apps\/([^/]+)/);
  return match
    ? `/app/assets/connections/${match[1]}`
    : "/app/assets/connections";
}

const ASSET_TYPE_LABEL: Record<string, string> = {
  zap: "Zap workflows",
  nextGenZap: "Next Gen Zaps",
  table: "Tables",
  interface: "Interfaces",
  canvas: "Canvases",
  agent: "Agents",
  chatbot: "Chatbots",
  folder: "Folders",
};

const ASSET_TYPE_ROUTE: Record<string, string> = {
  zap: "/app/assets/zaps",
  nextGenZap: "/app/assets/next-gen-zaps",
  table: "/app/assets/tables",
  interface: "/app/assets/templates",
  canvas: "/app/assets/canvases",
  agent: "/app/assets/agents",
  chatbot: "/app/assets/chatbots",
  folder: "/app/assets/folders",
};

interface SearchResultsProps {
  query: string;
  searchSections: SearchSectionType[];
  assetData: GlobalSearchOutput | undefined;
  /** Whether Porter results have resolved for the current query. */
  porterResolved: boolean;
  emit?: GlobalSearchEmitFunction;
}

/**
 * Builds the React Aria `Menu` collection for a resolved search: asset-type
 * sections (with a "Show all" link) followed by Porter sections. Non-item
 * content (guidepost, skeletons, empty states) is rendered by the dropdown
 * outside of the `Menu`.
 */
export function SearchResults({
  query,
  searchSections,
  assetData,
  porterResolved,
  emit,
}: SearchResultsProps) {
  const assetTypeEntries = assetData
    ? (
        Object.entries(assetData.resultsByType) as [
          SearchableAssetType,
          (typeof assetData.resultsByType)[SearchableAssetType],
        ][]
      ).filter(([, items]) => items.length > 0)
    : [];

  return (
    <>
      {assetTypeEntries.map(([type, items]) => {
        const label = ASSET_TYPE_LABEL[type] ?? capitalize(type);
        return (
          <SearchSection title={label} id={`asset-${type}`} key={type}>
            {items.map((result, index) => {
              const resultUrl = getAssetUrl(
                result.assetType,
                result.id,
                getResultUrl(result),
              );
              return (
                <SearchResult
                  key={`${result.assetType}-${result.id}`}
                  id={`asset-${result.assetType}-${result.id}`}
                  title={result.title || "Untitled"}
                  query={query}
                  href={resultUrl}
                  icon={getAssetIconByType(result.assetType)}
                  iconColor={getAssetIconColor(result.assetType)}
                  updatedAt={result.updatedAt}
                  folderName={result.folderName}
                  ownerName={result.ownerName}
                  ownerPhotoUrl={result.ownerPhotoUrl}
                  onAction={() => {
                    emit?.({
                      event: GlobalSearchEvent.SelectResult,
                      eventAction: "click",
                      section: "assets",
                      text: resultUrl,
                      customData: {
                        asset_type: type,
                        asset_id: result.id,
                        position: String(index + 1),
                      },
                    });
                  }}
                />
              );
            })}
            {items.length >= 5 && ASSET_TYPE_ROUTE[type] && (
              <MenuItem
                id={`asset-showall-${type}`}
                textValue={`Show all ${label} results`}
                href={`${ASSET_TYPE_ROUTE[type]}?search=${encodeURIComponent(query.trim())}`}
                className={styles.showMoreLink}
              >
                <div className={styles.resultIcon}>
                  <Icon
                    name="navSearch"
                    size={16}
                    color="TextWeakest"
                    isBlock
                  />
                </div>
                <Text type="Body1" color="TextDefault">
                  Show all {label} results
                </Text>
              </MenuItem>
            )}
          </SearchSection>
        );
      })}
      {porterResolved &&
        searchSections.map(({ key, icon, iconColor, data }) => {
          if (!data || !data.results || data.results.length === 0) {
            return;
          }

          let visibleResults = data.results.slice(0, 2);
          if (key === "templates") {
            visibleResults = data.results.slice(0, 3);
          }

          return (
            <SearchSection
              title={capitalize(data.resourceNamePlural || key)}
              displayHorizontal={key === "apps"}
              id={`porter-${key}`}
              key={key}
            >
              {visibleResults.map((result: SearchEntry, index: number) =>
                "id" in result && result.id !== undefined ? (
                  <SearchResult
                    key={result.id}
                    id={`porter-${key}-${result.id}`}
                    title={result.title || "Untitled"}
                    query={query}
                    href={
                      key === "apps"
                        ? getAppConnectionsUrl(result.url || "")
                        : result.url || "#"
                    }
                    icon={icon}
                    iconColor={iconColor}
                    appIcon={
                      "image" in result
                        ? (result.image ?? undefined)
                        : undefined
                    }
                    isApp={key === "apps"}
                    updatedAt={result.updatedAt}
                    onAction={() => {
                      emit?.({
                        event: GlobalSearchEvent.SelectResult,
                        eventAction: "click",
                        section: key,
                        text: result.title ?? "",
                        customData: {
                          asset_type: key,
                          asset_id: result.id,
                          position: String(index + 1),
                        },
                      });
                    }}
                  />
                ) : undefined,
              )}
            </SearchSection>
          );
        })}
    </>
  );
}
